import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {
  Box, Button, ButtonText, Heading, Icon, Modal,
  ModalBackdrop, ModalBody, ModalContent, ModalFooter, ModalHeader,
  Text, VStack,
} from '@gluestack-ui/themed';
import { CameraOff } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';

import { captureStillFromVideo } from '@/lecturer/SlotManager/ProctorExamPanel/LecturerPresenceSetup';

type WebcamPhase = 'rationale' | 'requesting' | 'live' | 'captured' | 'denied';

type WebcamCaptureModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (imageBase64: string) => void;
  onUploadInstead: () => void;
  getUserMedia?: typeof navigator.mediaDevices.getUserMedia;
};

function resolveGetUserMedia(
  override?: typeof navigator.mediaDevices.getUserMedia,
): typeof navigator.mediaDevices.getUserMedia | undefined {
  if (override) return override;
  if (typeof navigator === 'undefined') return undefined;
  return navigator.mediaDevices?.getUserMedia?.bind(navigator.mediaDevices);
}

function isCameraAvailable(getUserMedia?: typeof navigator.mediaDevices.getUserMedia): boolean {
  return typeof resolveGetUserMedia(getUserMedia) === 'function';
}

/**
 * Profile photo webcam capture with deny → retry → recover and upload fallback.
 */
export function WebcamCaptureModal({
  isOpen,
  onClose,
  onConfirm,
  onUploadInstead,
  getUserMedia,
}: WebcamCaptureModalProps) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [phase, setPhase] = useState<WebcamPhase>('rationale');
  const [capturedBase64, setCapturedBase64] = useState<string | null>(null);

  const mediaAccess = useCallback(
    () => resolveGetUserMedia(getUserMedia),
    [getUserMedia],
  );

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const requestCamera = useCallback(async () => {
    const getMedia = mediaAccess();
    if (!isCameraAvailable(getMedia)) {
      setPhase('denied');
      return;
    }
    setPhase('requesting');
    stopStream();
    try {
      const stream = await getMedia!({ video: true, audio: false });
      streamRef.current = stream;
      setPhase('live');
    } catch {
      setPhase('denied');
      stopStream();
    }
  }, [mediaAccess, stopStream]);

  useEffect(() => {
    if (!isOpen) {
      stopStream();
      setCapturedBase64(null);
      setPhase('rationale');
      return;
    }
    requestCamera();
  }, [isOpen, requestCamera, stopStream]);

  useEffect(() => {
    const video = videoRef.current;
    if (video && streamRef.current && phase === 'live') {
      video.srcObject = streamRef.current;
      video.play?.().catch(() => {});
    }
  }, [phase]);

  useEffect(() => {
    if (phase !== 'live' || !streamRef.current) return undefined;

    const handleTrackRevoked = () => {
      setCapturedBase64(null);
      stopStream();
      setPhase('denied');
    };

    const tracks = streamRef.current.getVideoTracks();
    tracks.forEach((track) => {
      track.addEventListener('ended', handleTrackRevoked);
      track.addEventListener('inactive', handleTrackRevoked);
    });

    return () => {
      tracks.forEach((track) => {
        track.removeEventListener('ended', handleTrackRevoked);
        track.removeEventListener('inactive', handleTrackRevoked);
      });
    };
  }, [phase, stopStream]);

  useEffect(() => () => stopStream(), [stopStream]);

  const handleTakePhoto = useCallback(async () => {
    if (!streamRef.current) return;
    try {
      const base64 = await captureStillFromVideo(videoRef.current as HTMLVideoElement);
      setCapturedBase64(base64);
      setPhase('captured');
      stopStream();
    } catch {
      setPhase('denied');
    }
  }, [stopStream]);

  const handleUsePhoto = useCallback(() => {
    if (!capturedBase64) return;
    onConfirm(capturedBase64);
    onClose();
  }, [capturedBase64, onClose, onConfirm]);

  const handleRetry = useCallback(() => {
    setCapturedBase64(null);
    requestCamera();
  }, [requestCamera]);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalBackdrop />
      <ModalContent maxWidth={480} testID="webcam-capture-modal">
        <ModalHeader>
          <Heading size="lg">{t('people.photo.capture.title')}</Heading>
        </ModalHeader>
        <ModalBody>
          <VStack space="md">
            <Text size="sm" color="$textSubtle">{t('people.photo.capture.body')}</Text>
            <Box
              w={200}
              h={200}
              alignSelf="center"
              borderRadius="$full"
              borderWidth={1}
              borderColor="$borderLight"
              bg="$bgSubtle"
              overflow="hidden"
              alignItems="center"
              justifyContent="center"
              testID="webcam-preview-circle"
            >
              {phase === 'denied' && (
                <VStack space="sm" alignItems="center" px="$3">
                  <Icon as={CameraOff} size="lg" color="$textSubtle" />
                  <Text size="xs" color="$textOnWarningSubtle" textAlign="center">
                    {t('people.photo.capture.deniedTitle')}
                  </Text>
                </VStack>
              )}
              {phase === 'requesting' && (
                <Text size="sm" color="$textSubtle">{t('people.photo.capture.preparing')}</Text>
              )}
              {phase === 'live' && (
                <video
                  ref={videoRef}
                  muted
                  playsInline
                  style={{
                    width: 200, height: 200, objectFit: 'cover', transform: 'scaleX(-1)',
                  }}
                />
              )}
              {phase === 'captured' && capturedBase64 && (
                <img
                  src={`data:image/jpeg;base64,${capturedBase64}`}
                  alt=""
                  style={{
                    width: 200, height: 200, objectFit: 'cover', transform: 'scaleX(-1)',
                  }}
                />
              )}
              {(phase === 'rationale' || (phase === 'denied' && !capturedBase64)) && phase !== 'denied' && (
                <Text size="sm" color="$textHint">{t('people.photo.capture.placeholder')}</Text>
              )}
            </Box>
            {phase === 'denied' && (
              <Box
                bg="$statusWarningSubtle"
                borderLeftWidth={4}
                borderLeftColor="$statusWarning"
                p="$3"
                testID="webcam-denied-note"
              >
                <Text size="sm" color="$textOnWarningSubtle">{t('people.photo.capture.deniedBody')}</Text>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <VStack space="sm" w="$full">
            {phase === 'captured' ? (
              <>
                <Button variant="outline" onPress={handleRetry} testID="webcam-retake">
                  <ButtonText>{t('people.photo.capture.retake')}</ButtonText>
                </Button>
                <Button action="primary" onPress={handleUsePhoto} testID="webcam-use-photo">
                  <ButtonText>{t('people.photo.capture.use')}</ButtonText>
                </Button>
              </>
            ) : (
              <>
                {phase === 'denied' ? (
                  <Button variant="outline" onPress={handleRetry} testID="webcam-retry">
                    <ButtonText>{t('people.photo.capture.retry')}</ButtonText>
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onPress={handleTakePhoto}
                    isDisabled={phase !== 'live'}
                    testID="webcam-take-photo"
                  >
                    <ButtonText>{t('people.photo.capture.take')}</ButtonText>
                  </Button>
                )}
                <Button
                  variant={phase === 'denied' ? 'solid' : 'link'}
                  action={phase === 'denied' ? 'primary' : undefined}
                  onPress={onUploadInstead}
                  testID="webcam-upload-instead"
                >
                  <ButtonText>{t('people.photo.capture.upload')}</ButtonText>
                </Button>
              </>
            )}
          </VStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
